import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(r => setUser(r.data))
      .catch(() => { localStorage.clear(); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    let nextUser = data.user;
    if (!nextUser?.permissions) {
      try {
        const me = await api.get('/auth/me');
        nextUser = me.data;
      } catch {}
    }

    setUser(nextUser);
    return nextUser;
  };

  const logout = async () => {
    const rt = localStorage.getItem('refresh_token');
    try { await api.post('/auth/logout', { refresh_token: rt }); } catch {}
    localStorage.clear();
    setUser(null);
  };

  const isProducer = user?.role === 'superadmin' || user?.role === 'producer_manager';
  const isSuperAdmin = user?.role === 'superadmin';
  const isDealerAdmin = user?.role === 'dealer_admin';

  const hasPerm = (section, perm) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;

    if (user.role === 'dealer_admin') {
      const defaults = {
        orders:   ['view','create','edit_content','view_prices'],
        repairs:  ['view','view_prices'],
        supplies: ['view','create','edit_content'],
      };
      const hasDefault = (defaults[section]||[]).includes(perm);
      const override = user.permissions?.find(p => p.section===section && p.perm===perm);
      return override ? override.granted : hasDefault;
    }

    return user.permissions?.some(p => p.section===section && p.perm===perm && p.granted) || false;
  };

  const canAccessReports = !!user && (user.role === 'superadmin' || user.role === 'dealer_admin');

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isProducer, isSuperAdmin, isDealerAdmin, hasPerm, canAccessReports }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
